import React, { useState, useMemo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, TablePagination, TextField, TableSortLabel, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { calculateVatAmount } from '../../../Utils/GetVatValue';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import CustomYearDatePicker from '../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';

function descendingComparator(a, b, orderBy) {
    if (a[orderBy] < b[orderBy]) {
        return 1;
    }
    if (a[orderBy] > b[orderBy]) {
        return -1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const SmallTransactionViewer = ({
    transactions = [],
    open = false,
    onClose = () => { },
    viewDocument,
    handleAssignDocument,
    assets,
    startDate,
    endDate,
    setStartDate,
    setEndDate
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Sorting
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');

    // Search
    const [searchTerm, setSearchTerm] = useState('');

    // ========== Pagination Handlers ==========
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // ========== Sorting Handlers ==========
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // ========== Date Handlers ==========
    const handleStartDateChange = (newValue, type) => {
        setStartDate(newValue, type);
    };
    const handleEndDateChangeNew = (newValue, type) => {
        setEndDate(newValue, type);
    };

    // ========== Search Handler ==========
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(0); // reset to first page on a new search
    };

    // Build a new array that includes participatingInDepreciation, isLocked, vatAmount, netAmount
    const enrichedTransactions = useMemo(() => {
        return transactions.map((tx) => {
            const assignedAsset = assets.find(
                (asset) => asset.expenseId === tx.id || asset.journalEntryId === tx.id
            );
            const vatAmount = calculateVatAmount(tx.amount, tx.vat, tx.documentDate);
            const netAmount = (tx.amount || 0) - vatAmount;
            return {
                ...tx,
                participatingInDepreciation: assignedAsset ? 'Yes' : 'No',
                isLocked: !!tx.isLocked,
                vatAmount,
                netAmount,
            };
        });
    }, [transactions, assets]);

    // Filter by search term
    const filteredTransactions = useMemo(() => {
        if (!searchTerm) return enrichedTransactions;

        const searchString = searchTerm.toLowerCase();
        return enrichedTransactions.filter((tx) => {
            const docDateString = tx.documentDate
                ? new Date(tx.documentDate).toLocaleDateString().toLowerCase()
                : '';
            const typeLabel = tx.type === 1 ? 'expense' : 'journalentry';

            const lockedLabel = tx.isLocked ? 'locked' : 'unlocked';
            return (
                (tx.description?.toLowerCase() || '').includes(searchString) ||
                (tx.reference?.toLowerCase() || '').includes(searchString) ||
                (tx.participatingInDepreciation?.toLowerCase() || '').includes(searchString) ||
                (tx.amount?.toString().toLowerCase() || '').includes(searchString) ||
                (tx.netAmount?.toString().toLowerCase() || '').includes(searchString) ||
                (tx.vatAmount?.toString().toLowerCase() || '').includes(searchString) ||
                docDateString.includes(searchString) ||
                typeLabel.includes(searchString) ||
                lockedLabel.includes(searchString)
            );
        });
    }, [enrichedTransactions, searchTerm]);

    // Sort after filtering
    const sortedTransactions = useMemo(() => {
        // If no column is selected for sorting, simply return the filtered array
        if (!orderBy) return filteredTransactions;
        return stableSort(filteredTransactions, getComparator(order, orderBy));
    }, [filteredTransactions, order, orderBy]);

    // Apply pagination
    const paginatedTransactions = useMemo(() => {
        return sortedTransactions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [sortedTransactions, page, rowsPerPage]);

    const assignedCount = useMemo(
        () => enrichedTransactions.filter((tx) => tx.participatingInDepreciation === 'Yes').length,
        [enrichedTransactions]
    );

    if (!open) return null;

    const columns = [
        { id: 'description', label: t('Description') },
        { id: 'reference', label: t('Reference') },
        { id: 'documentDate', label: t('documentDate') },
        { id: 'type', label: t('Type') },
        { id: 'amount', label: t('Amount') },
        { id: 'vatAmount', label: t('PCNVatAmount') },
        { id: 'netAmount', label: t('NetAmount') },
        { id: 'participatingInDepreciation', label: t('ParticipatingInDepreciation') },
        { id: 'isLocked', label: t('Locked') },
    ];

    return (
        <AssetModalShell
            open={open}
            onClose={onClose}
            title={t('EquipmentPurchaseTransactions')}
            direction={direction}
            size="xl"
            closeOnBackdrop
            toolbar={(
                <div className={shell.toolbar}>
                    <TextField
                        placeholder={t('Search')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        variant="standard"
                        className={shell.searchInput}
                        InputProps={{
                            disableUnderline: true,
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon /></InputAdornment>
                            ),
                        }}
                        inputProps={{ style: { textAlign: direction === 'rtl' ? 'right' : 'left' } }}
                    />
                    <CustomYearDatePicker
                        onDateChange={(date) => handleStartDateChange(date)}
                        date={startDate}
                        label={t('Year')}
                        height="var(--xtram-control-height, 40px)"
                        width="120px"
                    />
                    <div className={`${shell.stats} ${shell.toolbarEnd}`}>
                        <span className={shell.stat}>{t('Total') || 'סה"כ'}: <strong>{enrichedTransactions.length}</strong></span>
                        <span className={`${shell.stat} ${shell.statSuccess}`}>
                            {t('ParticipatingInDepreciation')}: <strong>{assignedCount}</strong>
                        </span>
                    </div>
                </div>
            )}
            footer={(
                <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                    {t('Close') || 'סגור'}
                </button>
            )}
        >
            <div className={shell.tableWrap}>
                <TableContainer sx={{ maxHeight: '55vh' }}>
                    <Table stickyHeader size="small" dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 48 }} />
                                {columns.map((col) => (
                                    <TableCell key={col.id} align="center" sortDirection={orderBy === col.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedTransactions.map((tx) => {
                                const isAssigned = tx.participatingInDepreciation === 'Yes';
                                return (
                                    <Tooltip key={tx.id} title={isAssigned ? t('TransactionIsAssignedToAsset') : ''} arrow>
                                        <TableRow
                                            hover
                                            className={`${shell.rowClickable}${isAssigned ? ` ${shell.rowSuccess}` : ''}`}
                                            onClick={() => handleAssignDocument(tx)}
                                        >
                                            <TableCell align="center">
                                                <Tooltip title={t('ViewDocument')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            viewDocument(tx.id, tx.type);
                                                        }}
                                                        sx={{ color: 'var(--xtram-blue)' }}
                                                    >
                                                        <DescriptionIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>{tx.description || '-'}</TableCell>
                                            <TableCell align="center">{tx.reference || '-'}</TableCell>
                                            <TableCell align="center">
                                                {tx.documentDate ? new Date(tx.documentDate).toLocaleDateString() : '-'}
                                            </TableCell>
                                            <TableCell align="center">{tx.type === 1 ? t('Expense') : t('JournalEntry')}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>{beautifyNumber(tx.amount || 0)}</TableCell>
                                            <TableCell align="center">{beautifyNumber(tx.vatAmount)}</TableCell>
                                            <TableCell align="center">{beautifyNumber(tx.netAmount)}</TableCell>
                                            <TableCell align="center">
                                                <span className={`${shell.badge} ${isAssigned ? shell.badgeSuccess : shell.badgeNeutral}`}>
                                                    {t(tx.participatingInDepreciation)}
                                                </span>
                                            </TableCell>
                                            <TableCell align="center">
                                                {tx.isLocked ? (
                                                    <Tooltip title={t('Locked')}>
                                                        <LockIcon sx={{ color: 'var(--xtram-mirage-muted)', fontSize: 18 }} />
                                                    </Tooltip>
                                                ) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    </Tooltip>
                                );
                            })}
                            {paginatedTransactions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1}>
                                        <div className={shell.emptyState}>{t('NoDataAvailable') || t('NoOptions')}</div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    className={shell.pagination}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={sortedTransactions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('RowsPerPage')}
                />
            </div>
        </AssetModalShell>
    );
};

export default SmallTransactionViewer;
